import { alertError, alertSuccess } from '../../../api'
import { LIMIT_REQUEST_CONTACT_EMAIL, USAGE_METRIC, USAGE_METRIC_META } from '../../../constants/usageLimits'
import { SUPERVISION_MODE, SUPERVISION_MODE_LABEL } from '../../../constants/supervisionMode'
import { QCS_ALLOWED_TABS, isTabHiddenForQcsManager } from '../../../constants/qcs'
import { isUsageStubEnabled, stubBulkUsageFor, stubSetLimits, stubUsageFor } from './usageLimitsDevStub'
import {
  requestPackageUsageLimitIncrease,
  getBotPackage,
  updateBotPackageConfig,
  requestSupervisionChange,
  requestCarePlanEnable,
  requestSupervisionEnable,
} from './botPackage'
import { overlayAssignmentOnUsage, fetchBotPackageAssignments, getCachedBotPackageAssignment } from '../helpers/orgPackages'

/** Map org Supervision dropdown → package assignment `supervision` DTO. */
export const supervisionModeToConfig = mode => ({
  defaultEnabled: true,
  dynamicEnabled: mode === SUPERVISION_MODE.DYNAMIC,
})

/** Map assignment `supervision` DTO → Default / Dynamic for org list UI. */
export const supervisionConfigToMode = supervision => {
  if (!supervision) return null
  if (supervision.dynamicEnabled) return SUPERVISION_MODE.DYNAMIC
  if (supervision.defaultEnabled) return SUPERVISION_MODE.DEFAULT
  return null
}

/**
 * Whether supervision features are on for this bot's package assignment.
 * No assignment (e.g. CareBrain) → true so existing UI is unchanged.
 * Both defaultEnabled and dynamicEnabled false → disabled.
 */
export const isBotPackageSupervisionEnabled = assignment => {
  if (!assignment) return true
  const supervision = assignment.supervision
  return Boolean(supervision?.defaultEnabled || supervision?.dynamicEnabled)
}

/**
 * Whether care-plan audits are on for this bot's package assignment.
 * No assignment → true so existing UI is unchanged.
 * Prefer `carePlanEnabled`; fall back to `usageLimit.enabled`.
 */
export const isBotPackageCarePlanEnabled = assignment => {
  if (!assignment) return true
  if (assignment.carePlanEnabled != null) return Boolean(assignment.carePlanEnabled)
  return Boolean(assignment.usageLimit?.enabled)
}

/**
 * Package assignment for nav gates — prefer embedded `packageDetails`, else cache.
 */
const assignmentForBot = bot =>
  bot?.packageDetails || getCachedBotPackageAssignment(bot?.id) || null

/**
 * Service Users rail/page: hide only when care-plan audit is off on the package
 * AND the org has never used it (`carePlanFeatureUsed !== true`). Grandfathers
 * orgs that already have audits after a package downgrade.
 * No bot / no assignment → true (CareBrain and unset package stay unchanged).
 */
export const isServiceUsersNavAllowed = bot => {
  if (!bot) return true
  if (isBotPackageCarePlanEnabled(assignmentForBot(bot))) return true
  return bot.carePlanFeatureUsed === true
}

/**
 * Professionals rail/page and Surveys & Supervision settings: hide only when
 * supervision is off on the package AND never used (`supervisionFeatureUsed`).
 * No bot / no assignment → true (CareBrain unchanged).
 */
export const isProfessionalsNavAllowed = bot => {
  if (!bot) return true
  if (isBotPackageSupervisionEnabled(assignmentForBot(bot))) return true
  return bot.supervisionFeatureUsed === true
}

/**
 * Like `getQcsLandingTab(role)` but skips Service Users / Professionals when
 * the package gate + *FeatureUsed helpers hide them. Falls back to the
 * role-only landing when `bot` is omitted.
 */
export const resolveQcsLandingTab = (role, bot) => {
  const hideForManager = role === 'ROLE_MANAGER'
  const tab = QCS_ALLOWED_TABS.find(path => {
    if (hideForManager && isTabHiddenForQcsManager(path)) return false
    if (bot) {
      if (path === 'serviceUsers' && !isServiceUsersNavAllowed(bot)) return false
      if (path === 'professionals' && !isProfessionalsNavAllowed(bot)) return false
    }
    return true
  })
  if (tab) return tab
  // No rail tab left (e.g. Care Manager with both features gated). Admins can
  // still open Settings; managers keep the historic professionals URL so Menu
  // PermissionDenied behaviour stays predictable.
  if (!hideForManager) return 'settings'
  return 'professionals'
}

/**
 * Default-only package mode (defaultEnabled, not dynamicEnabled).
 * No assignment → false so CareBrain / full Dynamic UI is unchanged.
 */
export const isDefaultOnlySupervision = assignment => {
  if (!assignment) return false
  const supervision = assignment.supervision || {}
  return Boolean(supervision.defaultEnabled) && !supervision.dynamicEnabled
}

/** QCS / platform "Standard Supervision" template (exact name match). */
export const isQcsStandardSupervisionTemplate = item =>
  /^(qcs\s+)?standard supervision$/i.test(String(item?.name || '').trim())

const usageLimitFromCarePlanLimit = limit => {
  if (limit == null) {
    return { enabled: true, unlimited: true }
  }
  return { enabled: true, unlimited: false, limit: Number(limit) }
}

/** Attach package feature flags (+ placeholders so request CTAs can render disabled). */
const withAssignmentUsageFeatures = (botId, assignment) => {
  const merged = overlayAssignmentOnUsage({}, { [botId]: assignment })
  const usage = merged[botId] || merged[String(botId)] || { metrics: {} }
  const metrics = { ...(usage.metrics || {}) }
  const carePlanEnabled = isBotPackageCarePlanEnabled(assignment)
  const supervisionEnabled = isBotPackageSupervisionEnabled(assignment)
  const usageLimit = assignment?.usageLimit || {}

  if (metrics.carePlanAudits == null) {
    metrics.carePlanAudits = {
      used: typeof assignment.usageConsumed === 'number' ? assignment.usageConsumed : 0,
      limit: carePlanEnabled ? null : 0,
    }
  } else if (
    metrics.carePlanAudits.limit === undefined &&
    !usageLimit.unlimited &&
    typeof usageLimit.limit === 'number'
  ) {
    // Overlay may have set `used` only; fill limit so exhausted can be true.
    metrics.carePlanAudits = {
      ...metrics.carePlanAudits,
      limit: usageLimit.limit,
    }
  }
  if (metrics.supervisionSessions == null) {
    metrics.supervisionSessions = { used: 0, limit: null }
  }

  return {
    ...usage,
    metrics,
    features: {
      [USAGE_METRIC.CARE_PLAN_AUDITS]: carePlanEnabled,
      [USAGE_METRIC.SUPERVISION_SESSIONS]: supervisionEnabled,
    },
  }
}

/**
 * Usage for one bot — from GET /bot/{id}/package (usageConsumed / usageLimit /
 * usageLimitResetAt). There is no /bot/{id}/usage endpoint in the package API.
 *
 * When the DEV usage stub is on, meter numbers come from the stub but package
 * feature flags still come from the real assignment so UI gates (e.g. hide
 * Supervision on usage tracking) match production.
 */
export function getBotUsage(botId, signal) {
  const stubFeaturesFallback = {
    [USAGE_METRIC.CARE_PLAN_AUDITS]: true,
    [USAGE_METRIC.SUPERVISION_SESSIONS]: true,
  }

  if (isUsageStubEnabled()) {
    const stub = stubUsageFor(botId)
    return getBotPackage(botId, signal)
      .then(assignment => {
        if (!assignment) {
          return { ...stub, features: stubFeaturesFallback }
        }
        const withFeatures = withAssignmentUsageFeatures(botId, assignment)
        return {
          ...stub,
          periodEnd: withFeatures.periodEnd || stub.periodEnd,
          features: withFeatures.features,
        }
      })
      .catch(error => {
        // Match non-stub: abort resolves to null so useBotUsage can ignore it
        // without an unhandled rejection on unmount.
        if (error?.name === 'AbortError') return null
        // Package fetch failed — keep stub meters; do not invent feature offs.
        return { ...stub, features: stubFeaturesFallback }
      })
  }

  return getBotPackage(botId, signal)
    .then(assignment => {
      if (!assignment) return null
      return withAssignmentUsageFeatures(botId, assignment)
    })
    .catch(() => null)
}

/**
 * Usage for the current page of QCS orgs — list-embedded `packageDetails`
 * cache only. Never calls GET /bot/{id}/package (Manage Limits open and
 * post-activation still fetch). No bulk /bots/usage call.
 */
export function getBulkUsage(botIds, signal) {
  if (!botIds || botIds.length === 0) return Promise.resolve({})
  if (isUsageStubEnabled()) return Promise.resolve(stubBulkUsageFor(botIds))

  return fetchBotPackageAssignments(botIds, signal, { cacheOnly: true }).then(assignments =>
    overlayAssignmentOnUsage({}, assignments),
  )
}

/**
 * Per-bot care-plan audit cap — PATCH /bot/{id}/package/config `usageLimit`.
 * `limits.carePlanAudits` is a number or null (unlimited).
 */
export function updateBotLimits(botId, limits) {
  if (isUsageStubEnabled()) {
    const next = stubSetLimits(botId, limits)
    alertSuccess('Monthly limits updated.', 2000)
    return Promise.resolve(next)
  }

  const usageLimit = usageLimitFromCarePlanLimit(limits?.[USAGE_METRIC.CARE_PLAN_AUDITS])

  return updateBotPackageConfig(botId, { usageLimit })
    .then(data => {
      alertSuccess('Monthly limits updated.', 2000)
      return data
    })
    .catch(err => {
      alertError(err?.message || 'Could not update the limits. Please try again or ping support.')
      throw new Error('usage-limits-update-failed')
    })
}

/**
 * Per-bot supervision schedule — PATCH /bot/{id}/package/config `supervision`.
 */
export function updateSupervisionMode(botId, supervisionMode) {
  if (isUsageStubEnabled()) {
    alertSuccess('Supervision mode updated.', 2000)
    return Promise.resolve({ supervisionMode })
  }

  return updateBotPackageConfig(botId, { supervision: supervisionModeToConfig(supervisionMode) })
    .then(data => {
      alertSuccess('Supervision mode updated.', 2000)
      return data
    })
    .catch(err => {
      alertError(err?.message || 'Could not update the supervision mode. Please try again or ping support.')
      throw new Error('supervision-mode-update-failed')
    })
}

export function requestLimitIncrease(
  botId,
  { metric, requestedLimit, requestedSupervisionMode, reason, botName, requestedBy } = {},
) {
  const isSupervisionRequest = metric === USAGE_METRIC.SUPERVISION_SESSIONS

  // Care-plan-audit asks — POST /bot/{id}/package/usage-limit/increase-request.
  if (!isSupervisionRequest && requestedLimit) {
    return requestPackageUsageLimitIncrease(botId, {
      message: (reason && String(reason).trim()) || 'Please increase our monthly care plan audit limit.',
      limit: Number(requestedLimit),
    })
      .then(({ message }) => {
        alertSuccess(
          message || 'Request sent. Someone will be in touch to confirm the new limit.',
          3000,
        )
        return true
      })
      .catch(err => {
        alertError(err?.message || 'Could not send the limit increase request.')
        openLimitIncreaseMailto({
          metric,
          requestedLimit,
          requestedSupervisionMode,
          reason,
          botName,
          requestedBy,
        })
        return false
      })
  }

  // Supervision mode change — email request only (does not update assignment).
  if (!requestedSupervisionMode) {
    alertError('Choose a supervision mode to request.')
    return Promise.resolve(false)
  }

  return requestSupervisionChange(botId, {
    note: reason,
    supervision: supervisionModeToConfig(requestedSupervisionMode),
  })
    .then(({ message }) => {
      alertSuccess(message || 'Supervision change request sent successfully.', 3000)
      return true
    })
    .catch(err => {
      alertError(err?.message || 'Could not send the supervision change request.')
      return false
    })
}

/** POST /bot/{id}/package/care-plan/enable-request — does not enable anything itself. */
export function requestCarePlanAuditAccess(botId) {
  return requestCarePlanEnable(botId)
    .then(({ message }) => {
      alertSuccess(message || 'Request sent. Your admin will be in touch to set care plan audits up.', 3000)
      return true
    })
    .catch(err => {
      alertError(err?.message || 'Could not send the care plan audit request.')
      return false
    })
}

/** POST /bot/{id}/package/supervision/enable-request — does not enable anything itself. */
export function requestSupervisionAccess(botId) {
  return requestSupervisionEnable(botId)
    .then(({ message }) => {
      alertSuccess(message || 'Request sent. Your admin will be in touch to set supervision up.', 3000)
      return true
    })
    .catch(err => {
      alertError(err?.message || 'Could not send the supervision request.')
      return false
    })
}

export function openLimitIncreaseMailto({
  metric,
  requestedLimit,
  requestedSupervisionMode,
  reason,
  botName,
  requestedBy,
} = {}) {
  const isSupervisionRequest = metric === USAGE_METRIC.SUPERVISION_SESSIONS
  const label = USAGE_METRIC_META[metric]?.label || 'usage'
  const modeLabel = SUPERVISION_MODE_LABEL[requestedSupervisionMode]
  const subject = isSupervisionRequest
    ? `CareBrain supervision mode change${botName ? ` — ${botName}` : ''}`
    : `CareBrain limit increase — ${label}${botName ? ` — ${botName}` : ''}`
  const body = [
    isSupervisionRequest
      ? `Please change our supervision mode${modeLabel ? ` to ${modeLabel}` : ''}.`
      : `Please increase our monthly ${label.toLowerCase()} limit.`,
    '',
    botName ? `Organisation: ${botName}` : '',
    !isSupervisionRequest && requestedLimit ? `Requested monthly limit: ${requestedLimit}` : '',
    isSupervisionRequest && modeLabel ? `Requested supervision mode: ${modeLabel}` : '',
    reason ? `Reason: ${reason}` : '',
    requestedBy ? `Requested by: ${requestedBy}` : '',
  ]
    .filter(Boolean)
    .join('\n')

  window.location.href =
    `mailto:${LIMIT_REQUEST_CONTACT_EMAIL}` +
    `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}
